import { TkitUtils as Utils } from '@tkit/types';
import createModel, { Tction } from '@tkit/model';
import uniqBy from 'lodash/uniqBy';
import produce from 'immer';
import { message } from 'antd';

import { IRootState, ListPane } from '@src/types';
import {
  APIS,
  UserMessageInfo,
  ParamsgroupsGroupIdJoinUsingPost,
  UserCondition,
  ParamsgroupsGroupIdAuditedOperaUsingPut
} from '@src/services';

import { PAGE_SIZE, NETWORK_ERROR_TIP, LIST_INITIAL } from '@src/utils/constant/common';

export type WithFollowingUserMessageInfo = UserMessageInfo & {
  isFollowing?: boolean;
};

export type WithIndexUserCondition = UserCondition & { index: number };
export type WithIndexParamsgroupsGroupIdJoinUsingPost = ParamsgroupsGroupIdJoinUsingPost & {
  index: number;
};
export type WithIndexParamsgroupsGroupIdAuditedOperaUsingPut = ParamsgroupsGroupIdAuditedOperaUsingPut & {
  index: number;
};
interface MessageModelState {
  all: ListPane<WithFollowingUserMessageInfo>;
  like: ListPane<WithFollowingUserMessageInfo>;
  comment: ListPane<WithFollowingUserMessageInfo>;
  reply: ListPane<WithFollowingUserMessageInfo>;
  at: ListPane<WithFollowingUserMessageInfo>;
  invite: ListPane<WithFollowingUserMessageInfo>;
  follow: ListPane<WithFollowingUserMessageInfo>;
  answer: ListPane<WithFollowingUserMessageInfo>;
  remind: ListPane<WithFollowingUserMessageInfo>;
  require: ListPane<WithFollowingUserMessageInfo>;
}

export const messageModelState: MessageModelState = {
  all: LIST_INITIAL,
  like: LIST_INITIAL,
  comment: LIST_INITIAL,
  reply: LIST_INITIAL,
  at: LIST_INITIAL,
  invite: LIST_INITIAL,
  follow: LIST_INITIAL,
  answer: LIST_INITIAL,
  remind: LIST_INITIAL,
  require: LIST_INITIAL
};

const model = createModel({
  state: messageModelState,
  namespace: 'messageModel',
  reducers: {
    resetMessageTabData: (state, action: Tction<{ tabIndex: string }>) => {
      return produce(state, draftState => {
        draftState[action.payload.tabIndex] = { ...LIST_INITIAL };
      });
    },
    updateUserMessageTabLoading: (
      state,
      action: Tction<{ tabIndex: string; loading: boolean }>
    ) => {
      const { tabIndex, loading } = action.payload;

      return produce(state, draftState => {
        draftState[tabIndex].loading = loading;
      });
    },
    updateUserMessageTabHasLoaded: (
      state,
      action: Tction<{ tabIndex: string; hasLoaded: boolean }>
    ) => {
      const { tabIndex, hasLoaded } = action.payload;

      return produce(state, draftState => {
        draftState[tabIndex].hasLoaded = hasLoaded;
      });
    },
    updateUserMessageTabIsEnded: (
      state,
      action: Tction<{ tabIndex: string; isEnded: boolean }>
    ) => {
      const { tabIndex, isEnded } = action.payload;

      return produce(state, draftState => {
        draftState[tabIndex].isEnded = isEnded;
      });
    },
    updateUserMessageTabList: (
      state,
      action: Tction<{
        tabIndex: string;
        list: UserMessageInfo[];
        pageNum?: number;
        total?: number;
      }>
    ) => {
      const { tabIndex, list, pageNum, total } = action.payload;

      const appendData = list.map((item: WithFollowingUserMessageInfo) => {
        item.isFollowing = false;

        return item;
      });

      return produce(state, draftState => {
        draftState[tabIndex].list = uniqBy([...appendData], 'id');
        draftState[tabIndex].pageNum = pageNum;
        draftState[tabIndex].total = total;
      });
    },
    /** 更改页号 */
    updateMessageTabListPage: (
      state,
      action: Tction<{
        tabIndex: string;
        pageNum: number;
      }>
    ) => {
      const { tabIndex, pageNum } = action.payload;
      return produce(state, draftState => {
        draftState[tabIndex].pageNum = pageNum;
      });
    },
    updateUserMessageIsFollowing: (
      state,
      action: Tction<{
        tabIndex: string;
        index: number;
        isFollowing: boolean;
      }>
    ) => {
      const { tabIndex, index, isFollowing } = action.payload;

      return produce(state, draftState => {
        draftState[tabIndex].list[index].isFollowing = isFollowing;
      });
    },
    updateUserMessageFollow: (
      state,
      action: Tction<{
        tabIndex: string;
        index: number;
        ifRelate?: boolean;
        agree?: number;
      }>
    ) => {
      const { tabIndex, index, agree = 0, ifRelate } = action.payload;

      return produce(state, draftState => {
        draftState[tabIndex].list[index].ifRelate = ifRelate;
        draftState[tabIndex].list[index].relation =
          agree === 1 ? 1 : agree === 2 ? 5 : draftState[tabIndex].list[index].relation;
      });
    }
  },
  effects: {
    doGetMessageTab: [
      function*(
        { tCall, tPut, select },
        action: Tction<{ tabIndex: string; activeTabKey: string; callBack?: () => void }>
      ): Iterator<{}, any, any> {
        const { tabIndex, activeTabKey, callBack } = action.payload;
        const info = yield select(
          (state: IRootState): ListPane<WithFollowingUserMessageInfo> => state.message[tabIndex]
        );

        yield tPut(model.actions.updateUserMessageTabLoading, {
          tabIndex,
          loading: true
        });

        const res: Utils.GetROA<typeof APIS.UserApi.userMessageUsingGet> = yield tCall(
          APIS.UserApi.userMessageUsingGet,
          {
            type: +activeTabKey,
            pageNum: info.pageNum + 1,
            pageSize: PAGE_SIZE
          }
        );
        yield tPut(model.actions.updateUserMessageTabHasLoaded, {
          tabIndex,
          hasLoaded: true
        });
        yield tPut(model.actions.updateUserMessageTabLoading, {
          tabIndex,
          loading: false
        });

        const { code, result } = res;

        if (code === 0 && result && Array.isArray(result.list) && result.list.length > 0) {
          // 通知中心有红点时候，点击铃铛加载出内容后，红点消失
          if (+activeTabKey === 0 && info.pageNum + 1 === 1) {
            callBack && callBack();
          }

          const { list, pageNum, total } = result;

          yield tPut(model.actions.updateUserMessageTabList, {
            tabIndex,
            list,
            pageNum,
            total
          });

          if (total && pageNum && Math.ceil(total / PAGE_SIZE) === pageNum) {
            yield tPut(model.actions.updateUserMessageTabIsEnded, {
              tabIndex,
              isEnded: true
            });
          }
        }
      },
      {
        type: 'takeEvery'
      }
    ],
    doUserMessageFollowPerson: [
      function*(
        { tCall, tPut },
        action: Tction<WithIndexUserCondition & { tabIndex: string }>
      ): Iterator<{}, any, any> {
        const { tabIndex, index, targetUser, ifFollow } = action.payload;

        yield tPut(model.actions.updateUserMessageIsFollowing, {
          tabIndex,
          index,
          isFollowing: true
        });

        const res: Utils.GetROA<typeof APIS.UserApi.userFollowUsingPost> = yield tCall(
          APIS.UserApi.userFollowUsingPost,
          {
            targetUser,
            ifFollow
          }
        );

        yield tPut(model.actions.updateUserMessageIsFollowing, {
          tabIndex,
          index,
          isFollowing: false
        });

        if (res.code === 0) {
          yield tPut(model.actions.updateUserMessageFollow, {
            tabIndex,
            index,
            ifRelate: ifFollow
          });

          message.success(ifFollow ? '已关注' : '已取消关注');
        } else {
          message.error(res.message || NETWORK_ERROR_TIP);
        }
      },
      {
        type: 'takeLatest'
      }
    ],
    doUserMessageFollowCircle: [
      function*(
        { tCall, tPut },
        action: Tction<WithIndexParamsgroupsGroupIdJoinUsingPost & { tabIndex: string }>
      ): Iterator<{}, any, any> {
        const { tabIndex, index, groupId, join } = action.payload;

        yield tPut(model.actions.updateUserMessageIsFollowing, {
          tabIndex,
          index,
          isFollowing: true
        });

        const res: Utils.GetROA<typeof APIS.GroupApi.groupsGroupIdJoinUsingPost> = yield tCall(
          APIS.GroupApi.groupsGroupIdJoinUsingPost,
          {
            groupId,
            join
          }
        );

        yield tPut(model.actions.updateUserMessageIsFollowing, {
          tabIndex,
          index,
          isFollowing: false
        });

        if (res.code === 0) {
          yield tPut(model.actions.updateUserMessageFollow, {
            tabIndex,
            index,
            ifRelate: join
          });

          message.success(join ? '已加入' : '已退出');
        } else {
          message.error(res.message || NETWORK_ERROR_TIP);
        }
      },
      {
        type: 'takeLatest'
      }
    ],
    /** 同意或拒绝加入圈子 */
    doUserJoinCircle: [
      function*(
        { tCall, tPut },
        action: Tction<
          WithIndexParamsgroupsGroupIdAuditedOperaUsingPut & {
            tabIndex: string;
            activeTabKey: string;
          }
        >
      ): Iterator<{}, any, any> {
        const { tabIndex, index, groupId, agree, username, activeTabKey } = action.payload;

        yield tPut(model.actions.updateUserMessageIsFollowing, {
          tabIndex,
          index,
          isFollowing: true
        });

        const res: Utils.GetROA<typeof APIS.GroupApi.groupsGroupIdAuditedOperaUsingPut> = yield tCall(
          APIS.GroupApi.groupsGroupIdAuditedOperaUsingPut,
          {
            groupId,
            agree,
            username
          }
        );

        yield tPut(model.actions.updateUserMessageIsFollowing, {
          tabIndex,
          index,
          isFollowing: false
        });

        if (res.code === 0) {
          yield tPut(model.actions.updateUserMessageFollow, {
            tabIndex,
            index,
            agree: agree ? 1 : 2
          });
          // yield tPut(model.actions.doGetMessageTab({ activeTabKey, tabIndex, update: true }));
          message.success(agree ? '已加入' : '已拒绝');
        } else {
          message.error(res.message || NETWORK_ERROR_TIP);
        }
      },
      {
        type: 'takeLatest'
      }
    ],
    *changeTabListPage(
      { tPut },
      action: Tction<{ tabIndex: string; activeTabKey: string; pageNum: number }>
    ): Iterator<{}> {
      const { tabIndex, activeTabKey, pageNum } = action.payload;
      yield tPut(model.actions.doGetMessageTab, { activeTabKey, tabIndex });
      yield tPut(model.actions.updateMessageTabListPage, {
        tabIndex,
        pageNum: pageNum - 1
      });
    }
  }
});

export default model;
